import {
  all,
  takeEvery,
  select,
  put,
  call,
  takeLatest,
  delay,
} from 'redux-saga/effects';
import {
  moveNext,
  movePrev,
  setCurrentItem,
  exitDuperbrowse,
  openDuperbrowse,
  shutdownDuperbrowse,
  exitDuperbrowseDone,
  loadDuperbrowseItem,
  loadDuperbrowse,
  openDuperbrowseWithEntities,
  unloadDuperbrowseItem,
  exitDuperbrowseResorationTimeout,
  setCurrentStreamByStackIndex,
} from './actionCreators';
import {
  getNextItem,
  getPrevItem,
  getCurrentStreamId,
  getCurrentItem,
  getRootLocation,
  getCurrentStreamSchema,
  getIsAnyStreamActive,
  getIsDuperbrowseActive,
  getIsHistoryManagementEnabled,
  getNeedsToLoadMore,
  getStackIndexOfStreamWithItem,
} from './selectors';
import {
  getStreamItemId,
  getStreamItemUrl,
  isDeviationStream,
  isDuperbrowseLocation,
  getNormalizedStreamItemId,
  getBiEventData,
} from './helpers';
import {
  duperbrowseHistoryChanged,
  historyChanged,
} from '../../redux/routing/actions';
import history from '../../redux/routing/history';
import { createLocation } from 'history';
import {
  DuperbrowseItem,
  DuperbrowseHistoryState,
  isSimpleStream,
} from './types';
import { handleLoading } from './loader';
import ddt from '@wix/da-ddt';
import { normalize } from 'normalizr';
import { deviationListSchema } from '../../redux/normalizr/schemas/deviation';
import { logBiEvent } from '../../biLogger/redux/saga';
import { getPageBiContext } from '../../biLogger/redux/selectors';
import { actions as streamActions } from '../../Stream';
import { scrollTo } from '../../utils/browser';

const logger = ddt.createLogger('duperbrowse');

function getNonDuperHistoryState() {
  const { duperbrowseState, duperbrowseRoot, ...restState } =
    history.location.state || ({} as DuperbrowseHistoryState);

  return restState;
}

function* updateHistoryWith(item: DuperbrowseItem) {
  const isEnabled = yield select(getIsHistoryManagementEnabled);
  if (!isEnabled) {
    return;
  }

  const streamId: ReturnType<typeof getCurrentStreamId> =
    yield select(getCurrentStreamId);
  const url = getStreamItemUrl(item);
  const itemId = getStreamItemId(item);

  if (!streamId || !url || !itemId) {
    return;
  }

  const state: DuperbrowseHistoryState = {
    duperbrowseState: {
      streamId,
      itemId,
    },
  };

  try {
    const newLocation = createLocation(url, state, undefined, history.location);
    history.push(newLocation);
  } catch (e) {
    // catching here to not stop the saga when the browser refuses to update the history
    // this is usually the browser stopping spam of history
  }
}

function* moveToItem(
  item: DuperbrowseItem | undefined,
  direction: 'next' | 'prev'
) {
  // TODO: deal with loading state
  if (!item) {
    return;
  }

  const biContext: ReturnType<typeof getPageBiContext> =
    yield select(getPageBiContext);
  const currentItem: ReturnType<typeof getCurrentItem> =
    yield select(getCurrentItem);
  const schema: ReturnType<typeof getCurrentStreamSchema> = yield select(
    getCurrentStreamSchema
  );
  const itemId = getNormalizedStreamItemId(item)!;

  logger.log(`Moving to ${direction} item`, item);

  if (currentItem) {
    yield call(logBiEvent, {
      ...biContext,
      ...getBiEventData(currentItem, direction, item),
    });
  }

  yield put(unloadDuperbrowseItem(currentItem));
  yield call(updateHistoryWith, item);

  yield all([
    put(setCurrentItem(itemId)),
    put(loadDuperbrowseItem(itemId, schema!, direction)),
  ]);

  const needsToLoadMore = yield select(getNeedsToLoadMore, direction);
  if (needsToLoadMore) {
    const streamId = yield select(getCurrentStreamId);
    yield put(streamActions.fetch(streamId, direction));
  }
}

export function* handleMoveNext() {
  const nextItem: ReturnType<typeof getNextItem> = yield select(getNextItem);
  yield call(moveToItem, nextItem, 'next');
}

export function* handleMovePrev() {
  const prevItem: ReturnType<typeof getPrevItem> = yield select(getPrevItem);
  yield call(moveToItem, prevItem, 'prev');
}

function* handleHistoryChange({
  payload: { location, action },
}: ReturnType<typeof duperbrowseHistoryChanged>) {
  // we only need to handle popstates
  if (action !== 'POP') {
    return;
  }

  // if there is no state, bail
  if (!location.state) {
    window.location.reload();
    return;
  }

  // if we arrived at our entry point, exit session wherever we were
  if (location.state.duperbrowseRoot === true) {
    const isDuperActive = yield select(getIsDuperbrowseActive);
    yield put(shutdownDuperbrowse());

    // clean up
    history.replace({ ...history.location, state: getNonDuperHistoryState() });

    // if duperbrowse is not active and we landed on a non-duper url,
    // reload
    const isDuperLocation = isDuperbrowseLocation(location, true);
    if (!isDuperActive && !isDuperLocation) {
      window.location.reload();
      return;
    }

    yield put(exitDuperbrowseDone());
    return;
  }

  // if there is no non-root state, bail
  if (!location.state.duperbrowseState) {
    window.location.reload();
    return;
  }

  const { streamId, itemId } = location.state.duperbrowseState;

  // make sure this item actually exists in our streams
  const newStreamIndex = yield select(
    getStackIndexOfStreamWithItem,
    streamId,
    itemId
  );
  if (newStreamIndex < 0) {
    window.location.reload();
  }

  const currentItem: ReturnType<typeof getCurrentItem> =
    yield select(getCurrentItem);

  yield all([
    put(unloadDuperbrowseItem(currentItem)),
    put(setCurrentStreamByStackIndex(newStreamIndex, itemId)),
  ]);
}

function* handleNonDuperHistoryChange({
  payload: { action },
}: ReturnType<typeof historyChanged>) {
  // we only need to handle popstates
  if (action !== 'POP') {
    return;
  }

  // if duper is not active, nothing to do here
  const isDuperActive = yield select(getIsAnyStreamActive);
  if (!isDuperActive) {
    return;
  }

  // everything is terrible, just reload for sanity
  window.location.reload();
}

function* handleLoadDuperbrowse({
  payload: { itemId, stream },
}: ReturnType<typeof loadDuperbrowse>) {
  const currentItem: ReturnType<typeof getCurrentItem> =
    yield select(getCurrentItem);
  if (currentItem) {
    yield put(unloadDuperbrowseItem(currentItem));
  }

  if (isSimpleStream(stream) && isDeviationStream(stream)) {
    const { result, entities } = normalize(stream.items, deviationListSchema);

    yield put(
      openDuperbrowseWithEntities(
        itemId,
        {
          ...stream,
          items: result,
          itemSchema: 'deviation',
        },
        entities
      )
    );
    return;
  }

  yield put(openDuperbrowse(itemId, stream));
}

function* handleOpenDuperbrowse({
  payload: { currentOpenItem, itemSchema },
}: ReturnType<typeof openDuperbrowse>) {
  const isHistoryManagementEnabled: ReturnType<
    typeof getIsHistoryManagementEnabled
  > = yield select(getIsHistoryManagementEnabled);
  const currentItem: ReturnType<typeof getCurrentItem> =
    yield select(getCurrentItem);

  if (!currentItem) {
    return;
  }

  logger.log('Opening duperbrowse', currentItem);
  const isRoot = !(
    history.location.state && history.location.state.duperbrowseState
  );
  if (isRoot && isHistoryManagementEnabled) {
    history.replace({
      ...history.location,
      state: { ...getNonDuperHistoryState(), duperbrowseRoot: true },
    });
  }

  yield all([
    call(updateHistoryWith, currentItem),
    put(loadDuperbrowseItem(currentOpenItem, itemSchema!, undefined)),
  ]);
  typeof window !== 'undefined' && scrollTo(window, 0, 0);
}

function* handleExitDuperbrowse() {
  const currentItem: ReturnType<typeof getCurrentItem> =
    yield select(getCurrentItem);

  if (!currentItem) {
    // no current item, restore to original location
    const rootLocation: ReturnType<typeof getRootLocation> =
      yield select(getRootLocation);

    if (rootLocation) {
      history.push(rootLocation);
    }

    yield put(exitDuperbrowseDone());
    return;
  }

  yield call(updateHistoryWith, currentItem);
}

// We removed the lastViewedItem after a timeout so
// we avoid any problems with scroll restoration if the component
// re-renders. This comes at cost of re-render :(
function* handleExitDuperbrowseDone() {
  yield delay(500);
  yield put(exitDuperbrowseResorationTimeout());
}

export default function* rootSaga() {
  yield all([
    takeEvery(moveNext, handleMoveNext),
    takeEvery(movePrev, handleMovePrev),
    takeEvery(duperbrowseHistoryChanged, handleHistoryChange),
    takeEvery(historyChanged, handleNonDuperHistoryChange),
    takeEvery(loadDuperbrowse, handleLoadDuperbrowse),
    takeEvery(openDuperbrowse, handleOpenDuperbrowse),
    takeEvery(exitDuperbrowse, handleExitDuperbrowse),
    takeLatest(loadDuperbrowseItem, handleLoading),
    takeLatest(exitDuperbrowseDone, handleExitDuperbrowseDone),
    takeLatest(shutdownDuperbrowse, handleExitDuperbrowseDone),
  ]);
}
